import React, { useContext } from 'react';
import styled from 'styled-components';

import InputSettingsField from './InputSettingsField';
import FieldStyleSettings from './FieldStyleSettings';
import MetadataSettings from './MetadataSettings';
import CheckboxSettingsField from './CheckboxSettingsField';
import Dropzone from '../../misc/Dropzone';
import SettingsTabs from './SettingsTabs';
import AccordionSettings from './AccordionSettings';
import FieldSettingsHeaderTitle from './FieldSettingsHeaderTitle';
import { StyledFieldLabel, StyledFieldWrapper } from './SettingsCommonElements';

import FileViews from '../../../constants/FileViews';
import FormStylesOptions from '../../../constants/FormStylesOptions';

import FieldModes from '../../../constants/FieldModes';
import FieldPresentationModes from '../../../constants/FieldPresentationModes';

import { getFieldThemeObject } from '../../../theme/getThemeObject';

import ThemeContext from '../../../theme/ThemeContext';

const StyledWrapper = styled.div`
  padding: 12px 0px;
  display: flex;
  flex-direction: column;
`;

const StyledLabel = styled(StyledFieldLabel)`
  display: inline-block;
`;

const FileSettings = ({

  id,
  name,
  styles,
  metadata,
  config,

  //Action handlers
  onChange,
  onUpload,

  //View helpers
  view,
  views,
  onToggleViewSync,

}) => {

  const theme = useContext(ThemeContext);

  const {
    //Title styles
    titleFontSize,
    titleFontColor,
    titleTextAlign,
    titleFontStyle,
    titleFontWeight,
    titleTextTransform,
    titleTextDecoration,

    //Field style
    fontSize,
    fontColor,
    fontStyle,
    fontWeight,
    textAlign,
    textTransform,
    textDecoration,
    textOverflow,
    borderColor,
    borderRadius,
    borderWidth,
    backgroundColor,
    padding,
    margin
  } = getFieldThemeObject({
    mode: FieldModes.fill,
    fieldPresentationMode: FieldPresentationModes.normal,
    fileStyles: styles,
    theme: theme?.field,
    sdkTheme: theme
  });

  const hasMobileView = !views ? false : views.find((view) => view.type === FileViews.mobile);

  const stylesNode = (
    <>
      <AccordionSettings
        isOpen
        title="Title"
        height={156}
      >
        <FieldStyleSettings
          id={`${id}-title`}
          {...{
            fontSize: titleFontSize,
            onFontSizeChange: (value) => onChange(id, { styles: { ...styles, titleFontSize: value } }),

            fontColor: titleFontColor,
            onFontColorChange: (value) => onChange(id, { styles: { ...styles, titleFontColor: value } }),

            textAlign: titleTextAlign,
            onTextAlignChange: (value) => onChange(id, { styles: { ...styles, titleTextAlign: value } }),

            fontStyle: titleFontStyle,
            onFontStyleChange: (value) => onChange(id, { styles: { ...styles, titleFontStyle: value } }),
            fontWeight: titleFontWeight || FormStylesOptions.fontWeightBold,
            onFontWeightChange: (value) => onChange(id, { styles: { ...styles, titleFontWeight: value } }),
            textTransform: titleTextTransform,
            onTextTransformChange: (value) => onChange(id, { styles: { ...styles, titleTextTransform: value } }),
            textDecoration: titleTextDecoration,
            onTextDecorationChange: (value) => onChange(id, { styles: { ...styles, titleTextDecoration: value } }),
          }}
        />
      </AccordionSettings>
      <AccordionSettings
        isOpen
        title="Text"
        height={156}
      >
        <FieldStyleSettings
          id={`${id}-field`}
          {...{

            fontSize,
            onFontSizeChange: (value) => onChange(id, { styles: { ...styles, fontSize: value } }),

            fontColor,
            onFontColorChange: (value) => onChange(id, { styles: { ...styles, fontColor: value } }),

            textAlign,
            onTextAlignChange: (value) => onChange(id, { styles: { ...styles, textAlign: value } }),

            fontStyle,
            onFontStyleChange: (value) => onChange(id, { styles: { ...styles, fontStyle: value } }),
            fontWeight,
            onFontWeightChange: (value) => onChange(id, { styles: { ...styles, fontWeight: value } }),
            textTransform,
            onTextTransformChange: (value) => onChange(id, { styles: { ...styles, textTransform: value } }),
            textDecoration,
            onTextDecorationChange: (value) => onChange(id, { styles: { ...styles, textDecoration: value } }),

            textOverflow,
            onTextOverflowChange: (value) => onChange(id, { styles: { ...styles, textOverflow: value } }),

          }}
        />
      </AccordionSettings>
      <AccordionSettings
        isOpen
        title="Block"
        height={200}
      >
        <FieldStyleSettings
          id={`${id}-field`}
          {...{

            borderColor,
            onBorderColorChange: (value) => onChange(id, { styles: { ...styles, borderColor: value } }),

            borderRadius,
            onBorderRadiusChange: (value) => onChange(id, { styles: { ...styles, borderRadius: value } }),

            borderWidth,
            onBorderWidthChange: (value) => onChange(id, { styles: { ...styles, borderWidth: value } }),

            backgroundColor,
            onBackgroundColorChange: (value) => onChange(id, { styles: { ...styles, backgroundColor: value } }),

            margin,
            onMarginChange: (value) => onChange(id, { styles: { ...styles, margin: value } }),

            padding,
            onPaddingChange: (value) => onChange(id, { styles: { ...styles, padding: value } }),

          }}
        />
      </AccordionSettings>
    </>
  );

  const settingsNode = (
    <>
      {view !== FileViews.mobile ? null : (
        <>
          <StyledFieldWrapper>
            <CheckboxSettingsField
              id="view-sync"
              label="Field Syncing Enabled"
              description="Custom mobile layouts disabled"
              selected={!hasMobileView}
              onChange={() => onToggleViewSync(FileViews.mobile)}
            />
          </StyledFieldWrapper>
        </>
      )}
      <StyledFieldWrapper>
        <InputSettingsField
          fieldId="name"
          value={name}
          label="Title"
          type="text"
          onChange={(fieldId, value) => onChange(id, { name: value })}
        />
      </StyledFieldWrapper>
      {!onUpload || view === FileViews.mobile ? null : (
        <StyledFieldWrapper>
          <StyledLabel>
            Upload PDF
          </StyledLabel>
          <Dropzone
            dragMessage=" "
            onDrop={(files) => onUpload(id, files[0])}
            accept={{ 'application/pdf': ['.pdf', '.PDF'] }}
          />
        </StyledFieldWrapper>
      )}
      {!config?.metadata ? null : (
        <>
          <MetadataSettings
            metadata={metadata}
            metadataOptions={config?.metadata}
            onChange={(changes) => {
              const nextMetadata = metadata ? { ...metadata, ...changes } : { ...changes };
              onChange(id, { metadata: nextMetadata });
            }}
          />
        </>
      )}
    </>
  );

  return (
    <StyledWrapper data-testid="form-settings">
      <FieldSettingsHeaderTitle title={view === FileViews.mobile ? 'Mobile Settings' : 'Settings'} />
      <SettingsTabs
        settings={settingsNode}
        styles={stylesNode}
      />
    </StyledWrapper>
  );

};

export default FileSettings;
